import { useEffect, useMemo, useState } from "react";
import {
  Bars3BottomLeftIcon,
  FlagIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

import {
  FilterSortHeader,
  FilterValues,
  TriStateFilterValue,
} from "../360/FilterSortHeader";
import { CheckTypeInfo } from "../api/Blueprints";
import { behaviorRelabelMapping, useFamaFlags } from "../api/FamaFlags";
import { REPORT_TYPES } from "../api/Reports";
import { checkTypeSourceNames } from "../check-types/checkTypeSources";
import { useRebuildIconBadgeTooltips } from "../check-types/iconBadgeTooltip";
import { ItemTypeBadge, ItemTypesCell } from "../check-types/itemTypeIcons";
import { slaDisplay } from "../check-types/slaDisplay";
import {
  SourceBadge,
  SourceCell,
  type SourceDisplayMode,
} from "../check-types/sourceIcons";
import BehaviorFlagBadge from "../shared-components/BehaviorFlagBadge";
import CheckBox from "../shared-components/CheckBox";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { HoverExpandCell } from "../shared-components/HoverExpandCell";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import PaginationInMemory from "../shared-components/PaginationInMemory";
import { sortNamedListAlpha } from "../utils";

const PAGE_SIZE = 10;
const COLLAPSED_COLUMN_WIDTH = "4rem";

type OptionalColumnKey = "sources" | "behaviors" | "itemTypes";

type HiddenColumns = Record<OptionalColumnKey, boolean>;

const DEFAULT_HIDDEN_COLUMNS: HiddenColumns = {
  sources: true,
  behaviors: true,
  itemTypes: true,
};

function assignModalGridTemplateColumns(hiddenColumns: HiddenColumns): string {
  return [
    "2.5rem",
    "1.8fr",
    hiddenColumns.sources ? COLLAPSED_COLUMN_WIDTH : "1fr",
    hiddenColumns.behaviors ? COLLAPSED_COLUMN_WIDTH : "1.5fr",
    hiddenColumns.itemTypes ? COLLAPSED_COLUMN_WIDTH : "1fr",
    "1.2fr",
  ].join(" ");
}

type CheckTypeAssignRow = CheckTypeInfo & {
  row_id: string;
  name_display: string;
  report_type_display: string;
  sla_type: string;
  lookback_display: string;
};

function reportTypeDisplay(reportType?: string): string {
  if (reportType === REPORT_TYPES.THREE_SIXTY) return "360";
  return "Misconduct";
}

function sourceNames(sources?: string[]): string[] {
  return checkTypeSourceNames(sources);
}

function activeBehaviorNames(behaviors?: string[]): string[] {
  return sortNamedListAlpha(Array.from(new Set(behaviors ?? [])));
}

function itemTypeNames(itemTypes?: string[]): string[] {
  return sortNamedListAlpha(Array.from(new Set(itemTypes ?? [])));
}

function buildRows(checkTypes: CheckTypeInfo[]): CheckTypeAssignRow[] {
  return checkTypes.map((checkType, index) => {
    const activeSources = sourceNames(checkType.sources);
    return {
      ...checkType,
      sources: activeSources,
      row_id: checkType.blueprint_uuid ?? `check-type-${index}`,
      name_display: checkType.name ?? "",
      report_type_display: reportTypeDisplay(checkType.report_type),
      sla_type: slaDisplay(checkType.turn_around, checkType.sla_hours),
      lookback_display: `${checkType.lookback} months`,
    };
  });
}

function rowSortValue(row: CheckTypeAssignRow, field: string): string | number {
  switch (field) {
    case "sources":
      return sourceNames(row.sources).join(", ");
    case "behaviors":
      return activeBehaviorNames(row.behaviors).join(", ");
    case "item_types":
      return itemTypeNames(row.item_types).join(", ");
    default: {
      const value = row[field as keyof CheckTypeAssignRow];
      if (typeof value === "number") {
        return value;
      }
      return String(value ?? "");
    }
  }
}

function compareRows(field: string, reverse: boolean) {
  return (a: CheckTypeAssignRow, b: CheckTypeAssignRow): number => {
    const aValue = rowSortValue(a, field);
    const bValue = rowSortValue(b, field);
    const result =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));
    return reverse ? -result : result;
  };
}

function filterRows(
  rows: CheckTypeAssignRow[],
  activeFilters: FilterValues[]
): CheckTypeAssignRow[] {
  return activeFilters.reduce((filteredRows, activeFilter) => {
    if (
      activeFilter.field === "sources" ||
      activeFilter.field === "behaviors" ||
      activeFilter.field === "item_types"
    ) {
      const triStateValues = activeFilter.values as TriStateFilterValue[];
      const included = triStateValues
        .filter((value) => value.state === "include")
        .map((value) => value.value);
      const excluded = triStateValues
        .filter((value) => value.state === "exclude")
        .map((value) => value.value);
      return filteredRows.filter((row) => {
        const values =
          activeFilter.field === "sources"
            ? sourceNames(row.sources)
            : activeFilter.field === "behaviors"
            ? activeBehaviorNames(row.behaviors)
            : itemTypeNames(row.item_types);
        return (
          included.every((value) => values.includes(value)) &&
          excluded.every((value) => !values.includes(value))
        );
      });
    }
    return filteredRows.filter((row) =>
      activeFilter.values.includes(
        row[activeFilter.field as keyof CheckTypeAssignRow]
      )
    );
  }, rows);
}

function BehaviorCountBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2 py-1 text-sm font-medium text-gray-700">
      {count}
      <FlagIcon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

function BehaviorFlagsCell({ behaviors }: { behaviors?: string[] }) {
  const { behaviorMappingActions } = useFamaFlags();
  const names = activeBehaviorNames(behaviors);
  const relabeledNames = behaviorRelabelMapping(names, behaviorMappingActions);

  if (relabeledNames.length === 0) {
    return <span className="text-gray-400">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {relabeledNames.map((name) => (
        <BehaviorFlagBadge key={name} name={name} />
      ))}
    </div>
  );
}

export type CompanyAddCheckTypeTableViewProps = {
  checkTypes: CheckTypeInfo[];
  selectedBlueprints: string[];
  onToggleSelect: (blueprintUUID: string) => void;
  isLoading: boolean;
  isError: boolean;
  isLoadingMore?: boolean;
};

export function CompanyAddCheckTypeTableView({
  checkTypes,
  selectedBlueprints,
  onToggleSelect,
  isLoading,
  isError,
  isLoadingMore = false,
}: CompanyAddCheckTypeTableViewProps) {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("name_display");
  const [reverse, setReverse] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterValues[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<HiddenColumns>(
    DEFAULT_HIDDEN_COLUMNS
  );
  const [sourcesDisplayMode, setSourcesDisplayMode] =
    useState<SourceDisplayMode>("text");

  const gridTemplateColumns = useMemo(
    () => assignModalGridTemplateColumns(hiddenColumns),
    [hiddenColumns]
  );

  const rows = useMemo(() => buildRows(checkTypes), [checkTypes]);

  const filteredRows = useMemo(
    () => filterRows(rows, activeFilters),
    [rows, activeFilters]
  );

  const sortedRows = useMemo(
    () => [...filteredRows].sort(compareRows(sortField, reverse)),
    [filteredRows, sortField, reverse]
  );

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const activePage = Math.min(page, totalPages);

  const activeRows = useMemo(
    () =>
      sortedRows.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE),
    [activePage, sortedRows]
  );

  useRebuildIconBadgeTooltips([
    activeRows,
    activePage,
    activeFilters,
    hiddenColumns,
    sourcesDisplayMode,
  ]);
  function setColumnHidden(column: OptionalColumnKey, hidden: boolean) {
    setHiddenColumns((current) => ({ ...current, [column]: hidden }));
  }

  function setSortBy(field: string) {
    if (!field) return;
    setPage(1);
    if (field === sortField) {
      setReverse(!reverse);
    } else {
      setSortField(field);
      setReverse(false);
    }
  }

  function setFilters(field: string, values: unknown[]) {
    setPage(1);
    const newFilters = activeFilters.filter((filter) => filter.field !== field);
    if (values.length > 0) {
      newFilters.push({ field, values });
    }
    setActiveFilters(newFilters);
  }

  if (isLoading || isLoadingMore) {
    return (
      <div className="py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError && checkTypes.length === 0) {
    return (
      <ErrorMessageContainer message="Cannot get check type information, please contact support" />
    );
  }

  return (
    <>
      <div className="overflow-x-auto min-w-[720px]">
        <div
          className="grid gap-2 text-gray-500 text-xs font-medium bg-gray-50 p-2 border rounded-t-xl border-gray-300 uppercase"
          style={{ gridTemplateColumns }}
        >
          <div />
          <FilterSortHeader
            headers={[
              {
                display: "Name",
                value_field: "name_display",
                filterable: true,
                sortable: true,
                searchable: true,
              },
              {
                display: "Report Type",
                value_field: "report_type_display",
                filterable: true,
                sortable: true,
              },
            ]}
            sortField={sortField}
            reverse={reverse}
            sortCallback={setSortBy}
            data={rows}
            activeFilters={activeFilters}
            setFilterCallback={setFilters}
          />
          <FilterSortHeader
            headers={[
              {
                display: "Sources",
                value_field: "sources",
                filterable: true,
                sortable: true,
                filterVariant: "listTriState",
                hideable: true,
                renderFilterOption: (value) => (
                  <SourceBadge sourceName={value} />
                ),
              },
            ]}
            sortField={sortField}
            reverse={reverse}
            sortCallback={setSortBy}
            data={rows}
            activeFilters={activeFilters}
            setFilterCallback={setFilters}
            hidden={hiddenColumns.sources}
            onHiddenChange={(hidden) => setColumnHidden("sources", hidden)}
            headerAccessory={
              <button
                type="button"
                aria-label={
                  sourcesDisplayMode === "icon"
                    ? "Show source names"
                    : "Show source icons"
                }
                title={
                  sourcesDisplayMode === "icon"
                    ? "Show source names"
                    : "Show source icons"
                }
                className="flex text-gray-400 hover:text-gray-600"
                onClick={(event) => {
                  event.stopPropagation();
                  setSourcesDisplayMode((current) =>
                    current === "icon" ? "text" : "icon"
                  );
                }}
              >
                {sourcesDisplayMode === "icon" ? (
                  <Bars3BottomLeftIcon className="h-4 w-4" />
                ) : (
                  <Squares2X2Icon className="h-4 w-4" />
                )}
              </button>
            }
          />
          <FilterSortHeader
            headers={[
              {
                display: "Behavior Flags",
                value_field: "behaviors",
                filterable: true,
                sortable: true,
                filterVariant: "listTriState",
                hideable: true,
              },
            ]}
            sortField={sortField}
            reverse={reverse}
            sortCallback={setSortBy}
            data={rows}
            activeFilters={activeFilters}
            setFilterCallback={setFilters}
            hidden={hiddenColumns.behaviors}
            onHiddenChange={(hidden) => setColumnHidden("behaviors", hidden)}
          />
          <FilterSortHeader
            headers={[
              {
                display: "Item Types",
                value_field: "item_types",
                filterable: true,
                sortable: true,
                filterVariant: "listTriState",
                hideable: true,
                renderFilterOption: (value) => (
                  <ItemTypeBadge itemTypeName={value} />
                ),
              },
            ]}
            sortField={sortField}
            reverse={reverse}
            sortCallback={setSortBy}
            data={rows}
            activeFilters={activeFilters}
            setFilterCallback={setFilters}
            hidden={hiddenColumns.itemTypes}
            onHiddenChange={(hidden) => setColumnHidden("itemTypes", hidden)}
          />
          <FilterSortHeader
            headers={[
              {
                display: "SLA Calc",
                value_field: "sla_type",
                filterable: true,
                sortable: true,
              },
              {
                display: "Lookback",
                value_field: "lookback",
                filterable: true,
                sortable: true,
                numeric: true,
              },
            ]}
            sortField={sortField}
            reverse={reverse}
            sortCallback={setSortBy}
            data={rows}
            activeFilters={activeFilters}
            setFilterCallback={setFilters}
          />
        </div>
        <div className="bg-white border rounded-b-xl border-gray-300 text-sm">
          {activeRows.length === 0 ? (
            <div className="p-4 text-gray-500 text-center">
              No check types available to assign
            </div>
          ) : (
            activeRows.map((row) => (
              <div
                key={row.row_id}
                data-testid="check-type-table-row"
                className="grid gap-2 border-b border-gray-100 p-2 last:border-b-0 items-center"
                style={{ gridTemplateColumns }}
              >
                <div className="flex justify-center">
                  <CheckBox
                    id={`table-assign-${row.blueprint_uuid}`}
                    checked={selectedBlueprints.includes(row.blueprint_uuid)}
                    onChange={() => onToggleSelect(row.blueprint_uuid)}
                  />
                </div>
                <div className="min-w-0 space-y-1">
                  <div className="font-medium text-gray-900 truncate">
                    {row.name_display}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row.report_type_display}
                  </div>
                </div>
                <div>
                  <HoverExpandCell
                    collapsed={hiddenColumns.sources}
                    collapsedContent={sourceNames(row.sources).length}
                    className={
                      hiddenColumns.sources ? "text-center text-xs" : ""
                    }
                    expandedContent={
                      <SourceCell
                        rowSourceNames={sourceNames(row.sources)}
                        displayMode={sourcesDisplayMode}
                      />
                    }
                  />
                </div>
                <div>
                  <HoverExpandCell
                    collapsed={hiddenColumns.behaviors}
                    collapsedContent={
                      <BehaviorCountBadge
                        count={activeBehaviorNames(row.behaviors).length}
                      />
                    }
                    className={
                      hiddenColumns.behaviors ? "text-center text-xs" : ""
                    }
                    expandedContent={
                      <BehaviorFlagsCell behaviors={row.behaviors} />
                    }
                  />
                </div>
                <div>
                  <HoverExpandCell
                    collapsed={hiddenColumns.itemTypes}
                    collapsedContent={itemTypeNames(row.item_types).length}
                    className={
                      hiddenColumns.itemTypes ? "text-center text-xs" : ""
                    }
                    expandedContent={
                      <ItemTypesCell itemTypes={row.item_types} />
                    }
                  />
                </div>
                <div className="space-y-1">
                  <div>{row.sla_type}</div>
                  <div className="text-xs text-gray-500">
                    {row.lookback_display}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {rows.length > 0 && (
        <PaginationInMemory
          currentPage={activePage}
          pageSize={PAGE_SIZE}
          totalCount={filteredRows.length}
          unfilteredCount={rows.length}
          clearFilters={() => {
            setActiveFilters([]);
            setPage(1);
          }}
          pageChange={(idx: number) => setPage(idx)}
          showDescription={true}
          activeStyle="bg-green-50"
        />
      )}
    </>
  );
}
